<script setup lang="ts">
import toRem from "@/helpers/shared/toRem";
import SVG from "@/stories/displays/SVG/SVG.vue";
import type { SVGName } from "@/SVGs";
import { computed, toRefs, type StyleValue } from "vue";

export type IconCircleVariant = "info" | "warning";
export type IconCircleSize = "xxs" | "xs" | "sm" | "md" | "lg" | "xl";

interface Props {
  icon?: SVGName;
  variant?: IconCircleVariant;
  size?: IconCircleSize;
  customStyle?: string;
}

const props = withDefaults(defineProps<Props>(), {
  variant: "info",
  size: "md",
});

toRefs(props);

const VARIANT_MAP: Record<IconCircleVariant, string> = {
  info: "bg-darkOcean100",
  warning: "bg-red400 text-white",
};

const toStyles = (size: string) => {
  const styles: Partial<StyleValue> = {};
  styles.minHeight = size;
  styles.minWidth = size;
  styles.height = size;
  styles.width = size;

  return styles;
};

const CONTAINER_SIZE_MAP: Record<IconCircleSize, number> = {
  xxs: 1.75,
  xs: 2,
  sm: 2.5,
  md: 3,
  lg: 3.75,
  xl: 6,
};

const iconSize = computed(() => {
  return `${(CONTAINER_SIZE_MAP[props.size] || 1) * 0.6}rem`;
});
</script>

<template>
  <div
    class="rounded-full flex justify-center items-center"
    :class="[customStyle ? customStyle : VARIANT_MAP[variant]]"
    :style="toStyles(toRem(CONTAINER_SIZE_MAP[size]))"
  >
    <slot>
      <SVG v-if="icon" :icon="icon" :height="iconSize" :width="iconSize" />
    </slot>
  </div>
</template>
